<template>
  <section class="loyalty-container small-margin-top-xs medium-margin-top-z margin-bottom-xl">
    <Skeleton v-if="!error && (!rewardsSummary || !firstName)" height="200px" />
    <Notification v-else-if="error" severity="error">
      <span v-html="error"></span>
    </Notification>
    <div v-else class="loyalty-section grid-x loyalty-pattern" :class="brand === 'bcom' && tierName ? `loyalty-pattern-${tierName}` : ''">
      <LoyaltyCelebrationFlourishes v-if="brand === 'mcom'" />
      <div class="loyalty-content grid-x padding-s">
        <div class="margin-bottom-xs cell grid-x small-12 align-justify">
          <div class="grid-x align-center align-middle loyalty-tier-container">
            <span v-if="brand === 'bcom'" :class="loyaltyIcon"></span>
            <img v-else :src="`${nuxtAppCDNUrl}/img/starRewards.svg`" fetchpriority="high" loading="eager" alt="Star Rewards Logo" />
            <div v-if="brand === 'mcom' && tierName">|</div>
            <div
              class="loyalty-tier body large"
              :class="{ 'loyalty-color': brand === 'bcom' && !tierName, [`loyalty-color-${tierName}`]: brand === 'bcom' && tierName }"
              data-fs-mask
            >
              {{ formattedTierName }}
            </div>
          </div>
          <button class="hide-for-large tooltip-wrapper">
            <Inbox :icon-size="'huge'" />
            <div class="css-tooltip caret-top" :class="showTooltip ? 'open' : ''">Inbox</div>
          </button>
        </div>
        <div class="cell small-12 medium-8 align-center-middle">
          <h2 class="margin-bottom-xs" data-fs-mask>
            {{ LOYALTY.salutation(firstName) }}
          </h2>
          <p v-if="!isBaseTier && tierName" class="medium" data-fs-mask>
            You’re {{ formattedTierName }} through {{ new Date(tierExpiryDate).toLocaleDateString() }}
          </p>
          <div v-if="!tierName" class="no-tier-content">
            <p class="margin-bottom-xs">
              {{ LOYALTY.noLoyaltyText }}
            </p>
            <p v-if="brand === 'bcom'" class="margin-bottom-xs">
              {{ LOYALTY.noLoyaltySignUpText }}
            </p>
            <a class="secondary button" :href="LOYALTY.joinNowLink">Join Now</a>
          </div>
        </div>
        <div class="cell grid-x small-12 medium-4 small-margin-top-s medium-margin-top-z">
          <div v-if="tierName" class="cell small-6">
            <h5 class="margin-bottom-xs">Points</h5>
            <p class="medium margin-bottom-xs" data-fs-mask>
              {{ LOYALTY.pointDetails(pointsToNextAward) }}
            </p>
            <a :href="LOYALTY.dashboardLink">{{ LOYALTY.dashboardText }}</a>
          </div>
          <div class="cell grid-x align-right align-middle" :class="{ 'small-6': tierName }">
            <LoyaltyTracker />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script setup>
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { Cookie } from '@vue-util/cookie';
import Skeleton from '@atomic-ui/skeleton/Skeleton';
import Notification from '@atomic-ui/notification/Notification';
import { brand, onlineUid, LOYALTY } from 'config';
import useLoyalty from '@/composables/useLoyalty';
import Inbox from '@/components/inbox/Inbox';
import { useProfileStore } from '@/stores/useProfileStore';
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';
import { useDashboardStore } from '@/stores/useDashboardStore';
import LoyaltyTracker from './LoyaltyTracker';
import LoyaltyCelebrationFlourishes from './LoyaltyCelebrationFlourishes.vue';

const envPropsStore = useEnvPropsStore();
const { nuxtAppCDNUrl } = storeToRefs(envPropsStore);
const showTooltip = ref(false);

const profileStore = useProfileStore();
const dashboardStore = useDashboardStore();
const { formattedTierName, firstName, tierExpiryDate, tierName, rewardsSummary, isBaseTier, pointsToNextAward, error, loyaltyIcon } = useLoyalty();

onMounted(async () => {
  const userId = Cookie.get(onlineUid);
  await Promise.all([dashboardStore.fetchRewardsSummary(), profileStore.fetchUserProfile(userId)]);
});
</script>
<style lang="scss" scoped>
@import '@core/csg/src/scss/components/tooltip';
@include csg-tooltip;
.css-tooltip.caret-top {
  left: 50%;
  margin-top: 4px;
  transform: translateX(-50%);
}
.tooltip-wrapper {
  position: relative;
  cursor: pointer;
}
.css-tooltip {
  display: none;
}
.tooltip-wrapper:hover .css-tooltip,
.tooltip-wrapper:focus-visible .css-tooltip {
  display: block;
}
.loyalty-section {
  background-color: $background-color;
  position: relative;
  overflow: hidden;
  .loyalty-content {
    width: 100%;
    position: relative;
    z-index: 2;
    .loyalty-tier-container {
      gap: rem-calc(6);
    }
    .no-tier-content {
      a {
        width: rem-calc(185);
        @include breakpoint(small only) {
          width: 100%;
        }
      }
    }
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
.loyalty-pattern {
  border-image: linear-gradient(to right, $loyalty-1-color 25%, $loyalty-2-color 25% 50%, $loyalty-3-color 50% 75%, $loyalty-4-color 75%);
  border-image-slice: 1;
  border-image-width: rem-calc(10) rem-calc(0) rem-calc(0) rem-calc(0);
}
.loyalty-tier {
  text-transform: capitalize;
}
</style>

<style lang="scss" brand="bcom" scoped>
.loyalty-pattern {
  border-top: rem-calc(10) solid $loyalty-1-color;
}
.loyalty-color {
  color: $loyalty-1-color;
}
$bcom-loyalty-tiers: (
  loyallist: $loyalty-1-color,
  tol: $loyalty-bg-3-color,
  tolunlocked: $loyalty-3-color,
);

@each $tier, $color in $bcom-loyalty-tiers {
  .loyalty-pattern-#{$tier} {
    border-top: rem-calc(10) solid $color;
  }
  .loyalty-color-#{$tier} {
    color: $color;
  }
}
.loyalty-tier {
  text-transform: uppercase;
}
</style>
